import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import {
  emailTemplateList,
  emailTemplateStatusChange,
} from "../../../../services/emailService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ManageEmailTemplateDrawer from "../../../../components/sections/admin/emailTemplate/drawer/ManageEmailTemplateDrawer";

const EmailTemplate = () => {
  setTitle("Email template");
  const [selectedItems, setSelectedItems] = useState([]);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const emailName = {
    "send-otp": "Send Otp",
    "welcome-email-user": "Welcome Email",
    "reset-pass-link-invite": "Reset Password",
    donation: "Donation",
    "send-contact-us-rec": "Contact Us",
    "email-change": "Change Email",
    scholarship_submission: "Scholarship Submission",
    application_status_track: "Application Status Track",
    application_status_reminder: "Application Status Reminder",
    "donor-send-invite": "Donor Send Invite",
    "contact-us-admin-notify": "Contact Us Admin Notify",
    "user-account-delete": "User Account Delete",
    scholarship_submission_admin_notify: "Scholarship Submission Admin Notify",
    "send-message": "Send Message",
    donation_received: "Donation Received",
  };

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["emailtemplate", filters, search],
    queryFn: async () => {
      const res = await emailTemplateList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   Application Form status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, isActive }) => {
        queryClient.setQueryData(
          ["emailtemplate", filters, search],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === id
                  ? {
                      ...elm,
                      isActive: isActive,
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          }
        );
        const res = await emailTemplateStatusChange({
          ids: [id],
          isActive: isActive,
        });
        return { res, payload: { id, isActive } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          queryClient.setQueryData(
            ["emailtemplate", filters, search],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = (stateData || []).map((elm) =>
                  elm?._id === payload?.id
                    ? {
                        ...elm,
                        isActive:
                          elm?.isActive === "active" ? "deactivated" : "active",
                      }
                    : elm
                );
                return { ...state, data: stateData };
              }
            }
          );
          toast.warning(res?.message);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "Email For",
      align: "left",
    },
    // {
    //   _id: 2,
    //   name: "Subject",
    //   align: "left",
    // },
    {
      _id: 3,
      name: "From Name",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
    },
    {
      _id: 5,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html: emailName[item?.code] || "-",
                }}
              ></div>
            ) || "-",
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data:
        //     (
        //       <div
        //         dangerouslySetInnerHTML={{
        //           __html: item?.subject || "-",
        //         }}
        //       ></div>
        //     ) || "-",
        // },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.from?.name || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.isActive === "active"}
                onChange={() => {
                  statusChangeHandler({
                    id: item?._id,
                    isActive:
                      item?.isActive === "active" ? "deactivated" : "active",
                  });
                }}
              />
              <Badge
                type={item?.isActive === "active" ? "success" : "danger"}
                label={item?.isActive}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  // add or edit Application Form =================>
  function emailFormManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(
            ["emailtemplate", filters, search],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = [res?.data, ...stateData];
                return { ...state, data: stateData };
              }
            }
          );
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["emailtemplate", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["emailtemplate", filters, search],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          }
        );
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-lg xl:text-3xl text-primary">
          Email Templates
        </div>

        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              buttonLabel={"Create New"}
              buttonLabelClasses={"font-medium !text-sm text-primary"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
      <ManageEmailTemplateDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={emailFormManageResponse}
      />
    </div>
  );
};

export default EmailTemplate;
